import { Breadcrumb, Card, Flex, Typography } from 'antd'
import type { BreadcrumbProps } from 'antd'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'

import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router/paths'

const { Paragraph, Text, Title } = Typography

type WorkspacePageShellProps = {
  sectionTitle?: string
  sectionDescription?: string
  /**
   * Adds a trailing item to the breadcrumb WITHOUT rendering an `<h4>`
   * section title. Use this on pages whose content already provides its own
   * hero/title (e.g. app detail, user detail), so the breadcrumb still ends
   * with the contextual name without duplicating it visually.
   */
  breadcrumbLeaf?: string
  /**
   * Intermediate breadcrumb items inserted between the org name and the
   * leaf. Use for deeper detail pages (e.g. inside an app: `Apps / CRM /
   * Credentials`). Each item may include `href` to become a link.
   */
  breadcrumbExtras?: BreadcrumbProps['items']
  /**
   * Optional content rendered beside the title block (e.g. KPI figures).
   * No effect on pages that don't pass it.
   */
  titleExtra?: ReactNode
  children: ReactNode
}

export const useWorkspaceRoute = () => {
  const { id = '' } = useParams<{ id: string }>()
  const workspaceId = Number(id)
  const idValid = id !== 'new' && Number.isFinite(workspaceId)

  return {
    id,
    workspaceId,
    idValid
  }
}

export const WorkspacePageShell = ({
  sectionTitle,
  sectionDescription,
  breadcrumbLeaf,
  breadcrumbExtras,
  titleExtra,
  children
}: WorkspacePageShellProps) => {
  const { t } = useTranslation()
  const { user } = useSessionContext()
  const { id, workspaceId, idValid } = useWorkspaceRoute()

  if (!idValid) {
    return <Navigate to={paths.workspaces.root} replace />
  }

  // Access (membership + role) is enforced upstream by WorkspaceAccessRoute, so
  // by the time this renders the workspace is in the session's organizations
  // list — read its name for the breadcrumb from there.
  const membership = user?.organizations?.find((o) => o.id === workspaceId)

  const leafLabel = breadcrumbLeaf ?? sectionTitle
  const breadcrumbItems: BreadcrumbProps['items'] = [
    { title: t('menu.workspaces'), href: paths.workspaces.root },
    { title: membership?.name ?? '', href: paths.workspaces.overview(id) },
    ...(breadcrumbExtras ?? []),
    ...(leafLabel ? [{ title: leafLabel }] : [])
  ]

  return (
    <Flex orientation="vertical" gap={8}>
      <Flex orientation="vertical" gap={12}>
        <Breadcrumb aria-label={t('ui.breadcrumb')} items={breadcrumbItems} />
        {sectionTitle || titleExtra ? (
          <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
            {sectionTitle ? (
              <div>
                <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
                  {sectionTitle}
                </Title>
                {sectionDescription ? (
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    {sectionDescription}
                  </Paragraph>
                ) : null}
              </div>
            ) : null}
            {titleExtra}
          </Flex>
        ) : null}
      </Flex>

      {children}
    </Flex>
  )
}

type WorkspaceSectionPlaceholderPageProps = {
  title: string
  description: string
}

export const WorkspaceSectionPlaceholderPage = ({
  title,
  description
}: WorkspaceSectionPlaceholderPageProps) => (
  <WorkspacePageShell sectionTitle={title} sectionDescription={description}>
    <Card variant="outlined">
      <Text type="secondary">{description}</Text>
    </Card>
  </WorkspacePageShell>
)

type WorkspaceAppPlaceholderPageProps = {
  title: string
  description: string
}

export const WorkspaceAppPlaceholderPage = ({
  title,
  description
}: WorkspaceAppPlaceholderPageProps) => {
  const { id = '', appId = '' } = useParams<{ id: string; appId: string }>()

  return (
    <WorkspacePageShell sectionTitle={title} sectionDescription={description}>
      <Card variant="outlined">
        <Flex orientation="vertical" gap={8}>
          <Text strong>App ID: {appId}</Text>
          <Text type="secondary">{description}</Text>
          <Text>
            Return to{' '}
            <a href={paths.workspaces.apps.root(id)}>workspace apps</a>.
          </Text>
        </Flex>
      </Card>
    </WorkspacePageShell>
  )
}
